import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, isToday, isTomorrow, isAfter, startOfDay } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';

const STATUS_COLORS = { pending: '#F59E0B', confirmed: '#10B981', completed: '#3B82F6', cancelled: '#F43F5E', reschedule_requested: '#D946EF' };

export default function OrgDashboard() {
  const { currentOrg } = useOrg();
  const { apiLocationFilter, currentLocation } = useLocations();
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentOrg) return;
    const apptParams = { include_collaborator: 'false' };
    if (apiLocationFilter) apptParams.location_id = apiLocationFilter;
    Promise.all([
      api.getAppointments(currentOrg.id, apptParams),
      api.getContacts(currentOrg.id),
      api.getStaff(currentOrg.id),
    ]).then(([a, c, s]) => {
      setAppointments(a);
      setContacts(c);
      setStaff(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentOrg, apiLocationFilter]);

  const today = new Date();
  const todayAppts = appointments.filter(a => isToday(new Date(a.starts_at)));
  const upcoming = appointments
    .filter(a => isAfter(new Date(a.starts_at), startOfDay(today)) && a.status !== 'cancelled')
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
    .slice(0, 6);
  const pendingCount = appointments.filter(a => a.status === 'pending').length;
  const confirmedToday = todayAppts.filter(a => a.status === 'confirmed').length;
  const rescheduleCount = appointments.filter(a => a.status === 'reschedule_requested').length;

  if (loading) {
    return (
      <div className="flex-1 bg-primary">
        <div className="max-w-[1100px] mx-auto px-4 md:px-7 py-6">
          <div className="h-7 w-48 bg-tertiary rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-tertiary rounded-xl animate-pulse" />)}
          </div>
          <div className="h-64 bg-tertiary rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  const dayLabel = (d) => {
    const date = new Date(d);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[1100px] mx-auto px-4 md:px-7 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c">Dashboard</h1>
            {currentLocation ? (
              <>
                <p className="text-[13px] font-semibold text-primary-c mt-0.5">{currentLocation.name}</p>
                <p className="text-[11px] text-muted-c">part of {currentOrg?.name}</p>
              </>
            ) : (
              <p className="text-[13px] text-muted-c mt-0.5">{currentOrg?.name}</p>
            )}
          </div>
          <Link to="/app/scheduling" className="hidden sm:flex items-center gap-2 px-4 py-2.5 gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow hover:-translate-y-0.5 transition-all">
            + New Appointment
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-elevated rounded-xl border border-theme p-4 flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-[12px] bg-accent-glow flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0DA7CA" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-muted-c uppercase tracking-wider">Today's Appointments</div>
              <div className="text-[26px] font-bold text-primary-c leading-tight mt-0.5">{todayAppts.length}</div>
              <div className="text-[11px] text-success font-medium">{confirmedToday} confirmed</div>
            </div>
          </div>
          <div className="bg-elevated rounded-xl border border-theme p-4 flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-[12px] bg-warning-soft flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-muted-c uppercase tracking-wider">Awaiting Confirmation</div>
              <div className="text-[26px] font-bold text-[#F59E0B] leading-tight mt-0.5">{pendingCount}</div>
              <div className="text-[11px] text-muted-c">need review</div>
            </div>
          </div>
          <div className="bg-elevated rounded-xl border border-theme p-4 flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-[12px] bg-success-soft flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-muted-c uppercase tracking-wider">Active Clients</div>
              <div className="text-[26px] font-bold text-primary-c leading-tight mt-0.5">{contacts.length}</div>
              <div className="text-[11px] text-muted-c">in your practice</div>
            </div>
          </div>
          <div className="bg-elevated rounded-xl border border-theme p-4 flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-[12px] bg-[rgba(34, 51, 104,0.1)] flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#223368" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-muted-c uppercase tracking-wider">Team Members</div>
              <div className="text-[26px] font-bold text-primary-c leading-tight mt-0.5">{staff.length}</div>
              <div className="text-[11px] text-muted-c">providers & staff</div>
            </div>
          </div>
        </div>

        {/* Reschedule alert */}
        {rescheduleCount > 0 && (
          <Link to="/app/scheduling" className="flex items-center gap-3 p-4 mb-4 bg-[rgba(217,70,239,0.08)] border border-[rgba(217,70,239,0.25)] rounded-xl hover:bg-[rgba(217,70,239,0.12)] transition-all">
            <div className="w-10 h-10 rounded-[10px] bg-[rgba(217,70,239,0.15)] flex items-center justify-center text-[14px] flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D946EF" strokeWidth="2"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-primary-c">{rescheduleCount} reschedule request{rescheduleCount > 1 ? 's' : ''}</div>
              <div className="text-[11px] text-muted-c">Clients are requesting to reschedule — review in scheduling</div>
            </div>
            <span className="text-[11px] font-semibold text-accent-light flex-shrink-0">Review &rarr;</span>
          </Link>
        )}

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
          {/* Upcoming appointments */}
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme">
              <h2 className="text-[14px] font-bold text-primary-c">Upcoming Appointments</h2>
              <Link to="/app/scheduling" className="text-[11px] font-semibold text-accent-light hover:underline">View calendar</Link>
            </div>
            {upcoming.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-muted-c">No upcoming appointments</div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {upcoming.map(appt => {
                  const statusColor = STATUS_COLORS[appt.status] || '#6B7280';
                  const clientName = appt.client ? `${appt.client.first_name} ${appt.client.last_name}` : 'No client';
                  const providerName = appt.provider ? `${appt.provider.first_name} ${appt.provider.last_name}` : 'Unassigned';
                  return (
                    <div key={appt.id} className="flex items-center gap-3.5 px-5 py-3 hover:bg-hover-surface transition-all">
                      <div className="w-[42px] text-center flex-shrink-0">
                        <div className="text-[16px] font-bold text-primary-c leading-none">{format(new Date(appt.starts_at), 'd')}</div>
                        <div className="text-[10px] font-semibold text-muted-c uppercase">{format(new Date(appt.starts_at), 'MMM')}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-primary-c truncate">{clientName}</div>
                        <div className="text-[11px] text-muted-c">{dayLabel(appt.starts_at)} &bull; {format(new Date(appt.starts_at), 'h:mm a')} &bull; {providerName}</div>
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white flex-shrink-0" style={{ background: statusColor }}>
                        {appt.status?.replace('_', ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick actions sidebar */}
          <div className="space-y-4">
            <div className="bg-elevated rounded-xl border border-theme p-5">
              <h3 className="text-[13px] font-bold text-primary-c mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/app/scheduling" className="flex items-center gap-2.5 p-2.5 rounded-[10px] hover:bg-hover-surface transition-all text-[12px] font-medium text-secondary-c">
                  <div className="w-8 h-8 rounded-lg bg-accent-glow flex items-center justify-center text-accent-light">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  View Schedule
                </Link>
                <Link to="/app/contacts" className="flex items-center gap-2.5 p-2.5 rounded-[10px] hover:bg-hover-surface transition-all text-[12px] font-medium text-secondary-c">
                  <div className="w-8 h-8 rounded-lg bg-success-soft flex items-center justify-center text-success">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                  </div>
                  Add Contact
                </Link>
                <Link to="/app/directory" className="flex items-center gap-2.5 p-2.5 rounded-[10px] hover:bg-hover-surface transition-all text-[12px] font-medium text-secondary-c">
                  <div className="w-8 h-8 rounded-lg bg-warning-soft flex items-center justify-center text-warning">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </div>
                  Browse Directory
                </Link>
                <Link to="/app/settings" className="flex items-center gap-2.5 p-2.5 rounded-[10px] hover:bg-hover-surface transition-all text-[12px] font-medium text-secondary-c">
                  <div className="w-8 h-8 rounded-lg bg-tertiary flex items-center justify-center text-muted-c">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </div>
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
